/**
 * Vocabulary review.
 *
 * This is the gate before item generation, not a settings screen. Every item is written
 * against a concept and every distractor is tagged to one, so a duplicated or wrongly
 * named concept quietly corrupts whole batches. The reviewer's job here is to prune:
 * approve what is real, merge what is duplicated, retire what does not belong.
 *
 * Two guards are surfaced rather than hidden, because both are irreversible in practice:
 *   - Renaming a concept that learners already have memory of would orphan their
 *     spaced-repetition history. The rename control is disabled and says why.
 *   - Deleting a concept that items reference would leave those items pointing at
 *     nothing. Delete is offered only when the concept is genuinely unused.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CheckIcon, ArrowsRightLeftIcon, ArchiveBoxIcon, TrashIcon, PencilIcon, ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../../config/api';
import { Eyebrow, ErrorNote, QueueSkeleton } from './primitives';

export interface ConceptRow {
  id: string;
  name: string;
  slug: string;
  subject: string | null;
  status: 'candidate' | 'approved' | 'retired';
  merged_into_id: string | null;
  item_count: number;
  learner_count: number;
  rename_blocked_reason: string | null;
  cell_tags: { a_id: number; b_id: number }[];
  cell_labels: string[];
}

export interface VocabularySummary {
  discipline: string;
  counts: Record<string, number>;
  subjects: string[];
  approved_without_cells: number;
}

const STATUS_FILTERS = [
  { value: 'candidate', label: 'Proposed' },
  { value: 'approved', label: 'Approved' },
  { value: 'retired', label: 'Retired' },
  { value: 'all', label: 'Everything' },
];

const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  candidate: { bg: 'var(--in-flagged-bg)', fg: 'var(--in-flagged)', label: 'Proposed' },
  approved: { bg: 'var(--in-correct-bg)', fg: 'var(--in-correct)', label: 'Approved' },
  retired: { bg: 'var(--in-skipped-bg)', fg: 'var(--in-skipped)', label: 'Retired' },
};

function errorText(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return [d.message, ...(d.problems || [])].join(' ');
  return e?.message || fallback;
}

const ConceptDesk: React.FC<{ exam: string; onChanged: () => void }> = ({ exam, onChanged }) => {
  const reduce = useReducedMotion();
  const [summary, setSummary] = useState<VocabularySummary | null>(null);
  const [rows, setRows] = useState<ConceptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [subject, setSubject] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [mergingId, setMergingId] = useState<string | null>(null);
  const [mergeTarget, setMergeTarget] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get<VocabularySummary>(`/api/item-review/${exam}/vocabulary`),
        api.get<ConceptRow[]>(`/api/item-review/${exam}/concepts`, {
          params: { status, subject: subject || undefined, search: search || undefined, limit: 500 },
        }),
      ]);
      setSummary(s.data);
      setRows(c.data);
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not load the vocabulary.'));
    } finally {
      setLoading(false);
    }
  }, [exam, status, subject, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const act = async (row: ConceptRow, action: string, extra: Record<string, any> = {}) => {
    setBusyId(row.id);
    setError(null);
    try {
      await api.post(`/api/item-review/${exam}/concepts/${row.id}/action`, { action, ...extra });
      setMergingId(null);
      setMergeTarget('');
      await load();
      onChanged();
    } catch (e) {
      setError(errorText(e, 'That action did not go through.'));
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async (row: ConceptRow) => {
    setBusyId(row.id);
    setError(null);
    try {
      const body: Record<string, any> = {};
      if (draftName.trim() && draftName.trim() !== row.name) body.name = draftName.trim();
      if (draftSubject !== (row.subject || '')) body.subject = draftSubject;
      if (Object.keys(body).length > 0) {
        await api.patch(`/api/item-review/${exam}/concepts/${row.id}`, body);
      }
      setEditingId(null);
      await load();
      onChanged();
    } catch (e) {
      setError(errorText(e, 'Could not save that change.'));
    } finally {
      setBusyId(null);
    }
  };

  const mergeCandidates = useMemo(
    () => rows.filter((r) => r.status !== 'retired'),
    [rows],
  );

  return (
    <div className="mx-auto w-full px-8 py-7 flex flex-col gap-5" style={{ maxWidth: 1000 }}>
      <div className="flex flex-col gap-2">
        <Eyebrow>Vocabulary</Eyebrow>
        <h2 className="text-[17px] font-semibold" style={{ color: 'var(--text-primary)' }}>
          Every item is written against one of these
        </h2>
        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary)', maxWidth: '72ch' }}>
          Approve the concepts that are real and testable, merge the duplicates, and retire what does
          not belong in entry-level practice. Generation only ever draws from approved concepts, so
          this list decides what the bank can be about.
        </p>
      </div>

      {summary && (
        <div className="flex flex-wrap items-center gap-5 py-3.5 px-4 in-surface">
          {(['candidate', 'approved', 'retired'] as const).map((k) => (
            <div key={k} className="flex flex-col gap-0.5">
              <span className="in-mono text-[18px] font-semibold" style={{ color: STATUS_STYLE[k].fg }}>
                {summary.counts[k] ?? 0}
              </span>
              <span className="in-eyebrow">{STATUS_STYLE[k].label}</span>
            </div>
          ))}
          {summary.approved_without_cells > 0 && (
            <div className="ml-auto text-[12.5px] leading-snug" style={{ color: 'var(--in-flagged)', maxWidth: '42ch' }}>
              {summary.approved_without_cells} approved concept{summary.approved_without_cells === 1 ? '' : 's'} are
              not yet placed in any exam area, so generation cannot target them.
            </div>
          )}
        </div>
      )}

      {error && <ErrorNote message={error} onRetry={load} />}

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => {
          const active = status === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              aria-pressed={active}
              className="in-focus text-[12px] font-medium px-2.5 py-1.5"
              style={{
                borderRadius: 'var(--in-r-control)',
                border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--in-line)'}`,
                background: active ? 'var(--in-accent-50)' : 'var(--bg-secondary)',
                color: active ? 'var(--accent-secondary)' : 'var(--text-secondary)',
              }}
            >
              {f.label}
            </button>
          );
        })}
        {summary && summary.subjects.length > 0 && (
          <select
            className="in-input in-focus"
            style={{ width: 'auto', padding: '7px 10px', fontSize: 12.5 }}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            aria-label="Filter by subject"
          >
            <option value="">All subjects</option>
            {summary.subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <input
          type="search"
          className="in-input in-focus"
          style={{ width: 220 }}
          placeholder="Search concepts"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search concepts"
        />
        <span className="in-mono text-[11px] ml-auto" style={{ color: 'var(--text-tertiary)' }}>
          {loading ? '' : `${rows.length} shown`}
        </span>
      </div>

      {loading && <QueueSkeleton />}

      {!loading && rows.length === 0 && (
        <div className="py-14 text-center flex flex-col gap-2">
          <span className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            No concepts match
          </span>
          <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            Widen the filter, or seed the vocabulary for this exam.
          </span>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <ul className="flex flex-col in-surface" style={{ padding: 0, overflow: 'hidden' }}>
          {rows.map((row, i) => {
            const st = STATUS_STYLE[row.status];
            const isEditing = editingId === row.id;
            const isMerging = mergingId === row.id;
            const busy = busyId === row.id;
            const canDelete = row.item_count === 0 && row.learner_count === 0;
            return (
              <motion.li
                key={row.id}
                initial={reduce ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, delay: Math.min(i * 0.012, 0.2) }}
                className="flex flex-col gap-3 px-4 py-3.5"
                style={{ borderTop: i === 0 ? 'none' : '1px solid var(--in-line)' }}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <span
                    className="in-chip flex-none"
                    style={{ background: st.bg, color: st.fg, borderColor: 'transparent' }}
                  >
                    {st.label}
                  </span>

                  <div className="flex-1 min-w-[240px] flex flex-col gap-1">
                    {isEditing ? (
                      <div className="flex flex-col gap-2">
                        <input
                          className="in-input in-focus"
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          disabled={!!row.rename_blocked_reason}
                          aria-label="Concept name"
                        />
                        <input
                          className="in-input in-focus"
                          style={{ maxWidth: 240 }}
                          value={draftSubject}
                          placeholder="Subject"
                          onChange={(e) => setDraftSubject(e.target.value)}
                          aria-label="Subject"
                        />
                        {row.rename_blocked_reason && (
                          <span className="text-[11.5px] leading-snug" style={{ color: 'var(--in-flagged)' }}>
                            {row.rename_blocked_reason}
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <span className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>
                          {row.name}
                        </span>
                        <span className="in-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                          {row.slug}{row.subject ? ` · ${row.subject}` : ''}
                        </span>
                        {row.cell_labels.length > 0 && (
                          <span className="text-[11.5px]" style={{ color: 'var(--text-secondary)' }}>
                            {row.cell_labels.join('; ')}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-0.5 flex-none" style={{ minWidth: 96 }}>
                    <span className="in-mono text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                      {row.item_count} item{row.item_count === 1 ? '' : 's'}
                    </span>
                    {row.learner_count > 0 && (
                      <span className="in-mono text-[11px]" style={{ color: 'var(--in-flagged)' }}>
                        {row.learner_count} learner{row.learner_count === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {isEditing ? (
                    <>
                      <button type="button" className="in-btn in-btn--primary in-focus" disabled={busy}
                        onClick={() => saveEdit(row)}>Save</button>
                      <button type="button" className="in-btn in-focus" disabled={busy}
                        onClick={() => setEditingId(null)}>Cancel</button>
                    </>
                  ) : isMerging ? (
                    <>
                      <select
                        className="in-input in-focus"
                        style={{ width: 'auto', maxWidth: 320, padding: '8px 10px', fontSize: 12.5 }}
                        value={mergeTarget}
                        onChange={(e) => setMergeTarget(e.target.value)}
                        aria-label="Merge into"
                      >
                        <option value="">Merge into which concept?</option>
                        {mergeCandidates.filter((c) => c.id !== row.id).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <button
                        type="button" className="in-btn in-btn--primary in-focus"
                        disabled={!mergeTarget || busy}
                        onClick={() => act(row, 'merge', { merge_into_id: mergeTarget })}
                      >
                        Merge and retire this one
                      </button>
                      <button type="button" className="in-btn in-focus" disabled={busy}
                        onClick={() => { setMergingId(null); setMergeTarget(''); }}>Cancel</button>
                      <span className="text-[11.5px]" style={{ color: 'var(--text-tertiary)' }}>
                        Items using this concept are repointed to the target.
                      </span>
                    </>
                  ) : (
                    <>
                      {row.status === 'candidate' && (
                        <button type="button" className="in-btn in-btn--approve in-focus" disabled={busy}
                          onClick={() => act(row, 'approve')}>
                          <CheckIcon className="w-4 h-4" aria-hidden="true" />
                          Approve
                        </button>
                      )}
                      {row.status === 'retired' && (
                        <button type="button" className="in-btn in-focus" disabled={busy}
                          onClick={() => act(row, 'reopen')}>
                          <ArrowUturnLeftIcon className="w-4 h-4" aria-hidden="true" />
                          Reopen
                        </button>
                      )}
                      <button
                        type="button" className="in-btn in-focus" disabled={busy}
                        title={row.rename_blocked_reason || 'Rename or change the subject'}
                        onClick={() => { setEditingId(row.id); setDraftName(row.name); setDraftSubject(row.subject || ''); }}
                      >
                        <PencilIcon className="w-4 h-4" aria-hidden="true" />
                        Edit
                      </button>
                      <button type="button" className="in-btn in-focus" disabled={busy}
                        onClick={() => setMergingId(row.id)}>
                        <ArrowsRightLeftIcon className="w-4 h-4" aria-hidden="true" />
                        Merge
                      </button>
                      {row.status !== 'retired' && (
                        <button type="button" className="in-btn in-focus" disabled={busy}
                          onClick={() => act(row, 'retire')}>
                          <ArchiveBoxIcon className="w-4 h-4" aria-hidden="true" />
                          Retire
                        </button>
                      )}
                      <button
                        type="button"
                        className="in-btn in-btn--reject in-focus"
                        disabled={busy || !canDelete}
                        title={canDelete
                          ? 'Permanently remove this concept'
                          : 'In use, so it can only be retired. Retiring keeps the history intact.'}
                        onClick={() => act(row, 'delete')}
                      >
                        <TrashIcon className="w-4 h-4" aria-hidden="true" />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </motion.li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ConceptDesk;
